// External dependencies
import React, { ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";

// Material-UI components
import {
  Alert,
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";

// Internal dependencies
import RequiredLabel from "pages/TargetForm/RequiredLabel";
import Spinner from "components/Spinner";
import { _addUser, _updateUser } from "middlewares/ManageUsers/manageusers";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import { userRoleModel } from "./data/userRoleModel";
import { UserRole, userRoles } from "./data/userRoles";
import { useAppState } from "context/appState.context";
import { UserRoles } from "constants/constant";
import { UserRoleConfigurationModel } from "./data/UserRoleConfigurationModel";
import Blurb from "components/Blurb/Blurb";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

// Interface for the props of the AddUser component
interface AddUserProps {
  handleCloseSnackBar: (state: AlertsProps) => void;
  hideDialogHandler: () => void;
  getUserRoles: () => void;
  rowData?: userRoleModel | null;
  userRoleSettings?: UserRoleConfigurationModel;
  getUserRoleSettings: () => void;
}

/**
 * AddUser component
 *
 * This component renders a form for adding or updating a user. It handles form submission, input changes, and API calls for saving user data.
 *
 * @param {AddUserProps} props - The props for the component.
 * @param {Function} props.handleCloseSnackBar - Function to handle closing the snackbar.
 * @param {Function} props.hideDialogHandler - Function to hide the dialog.
 * @param {Function} props.getUserRoles - Function to fetch user roles.
 * @param {userRoleModel | null} [props.rowData] - Optional data for pre-filling the form when editing a user.
 * @returns {JSX.Element} A form for adding or updating a user.
 */
const AddUser = (props: AddUserProps) => {
  // Extracting LocationId from URL parameters
  const { LocationId } = useParams();
  const parsedLocationId = Number(LocationId);

  // State for user data and loading status
  const [user, setUser] = useState<userRoleModel>({
    userRoleId: 0,
    userName: "",
    userEmail: "",
    company: "",
    roleId: "",
    locationId: parsedLocationId,
    locationMapping: parsedLocationId.toString(),
  });
  const [loading, setLoading] = useState(false);
  const [isUserRoleConfigured, setIsUserRoleConfigured] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);

  const { register, handleSubmit, setValue } = useForm();
  const { userRole } = useAppState();

  useEffect(() => {
    if (props.rowData) {
      setUser(props.rowData);
      setValue("userName", props.rowData.userName);
      setValue("userEmail", props.rowData.userEmail);
      setValue("company", props.rowData.company);
      setValue("roleId", props.rowData.roleId?.toString() ?? "");
    }
  }, [props.rowData, setValue]);

  // Handle input change for text fields
  /**
   * Handles input changes for text fields.
   *
   * @param {ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - The change event.
   */
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  // Handle select change for Role dropdown
  /**
   * Handles select changes for the Role dropdown.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | SelectChangeEvent<number>} event - The change event.
   */
  const handleRoleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
      | SelectChangeEvent<number>
  ) => {
    setUser({
      ...user,
      [event.target.name]: event.target.value,
    });
  };

  /**
   * Handles saving or updating a user by calling the appropriate API function.
   *
   * @param {Function} apiFunction - The API function to call (_addUser or _updateUser).
   */
  const handleSaveOrUpdate = (apiFunction: Function) => {
    if (typeof apiFunction === "function") {
      setLoading(true);
      setFormErrorMessage(null);
      apiFunction(user)
        .then((res: userRoleModel) => {
          if (res) {
            setLoading(false);
            const message =
              user.userRoleId === 0
                ? `${user.userName} Created Successfully !`
                : "Data Saved Successfully !";
            props.handleCloseSnackBar({
              show: true,
              severity: "success",
              message: message,
            });
            props.hideDialogHandler();
            props.getUserRoles();
            props.getUserRoleSettings();
          }
        })
        .catch((err: any) => {
          const message = getFriendlyErrorMessage(
            err,
            "We could not save the user right now. Please try again."
          );
          setFormErrorMessage(message);
          reportUiError(err, {
            feature: "UserManagement",
            action: user.userRoleId > 0 ? "update user" : "add user",
            component: "AddUser",
            route: typeof window !== "undefined" ? window.location.pathname : undefined,
            extra: {
              locationId: parsedLocationId,
              userRoleId: user.userRoleId,
            },
          });
          setLoading(false);
        });
    }
  };

  const checkUserRoleConfigured = (
    roleId: string | number,
    roleSettings: UserRoleConfigurationModel | undefined
  ): boolean => {
    if (!roleSettings) return false;

    switch (roleId) {
      case UserRole.Admin.toString():
        return roleSettings.adminCount + 1 > roleSettings.numberOfAdmins;
      case UserRole.StandardUser.toString():
        return (
          roleSettings.standardUsersCount + 1 >
          roleSettings.numberOfStandardUsers
        );
      case UserRole.Viewer.toString():
        return roleSettings.viewersCount + 1 > roleSettings.numberOfViewers;
      default:
        return false;
    }
  };

  /**
   * Determines whether to save or update the user based on the userRoleId.
   */
  const onSave = () => {
    const userRoleConfigured = checkUserRoleConfigured(
      user.roleId,
      props.userRoleSettings
    );

    setIsUserRoleConfigured(userRoleConfigured);

    if (!userRoleConfigured) {
      handleSaveOrUpdate(user.userRoleId > 0 ? _updateUser : _addUser);
    }
  };

  // JSX structure for the AddUser component
  return (
    <>
      <form onSubmit={handleSubmit(onSave)} data-testid="form">
        <Box
          sx={{
            width: "100%",
            overflow: "visible",
            position: "relative",
            minHeight: 250,
          }}
        >
        {formErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="add-user-error-alert">
            {formErrorMessage}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ mb: 2 }}>
            <RequiredLabel
              labelText="Display Name"
              isHelperTextRequired={false}
              content=""
            />
            <TextField
              inputProps={{ "data-testid": "userName", style: { fontSize: 12, padding: '4px 6px', height: 30 } }}
              {...register("userName", {
                required: true,
              })}
              required
              name="userName"
              value={user.userName ?? ""}
              size="small"
              label="Display Name"
              fullWidth
              onChange={handleInputChange}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
            />
          </Grid>
          <Grid item xs={6} sx={{ mb: 2 }}>
            <RequiredLabel
              labelText="Email"
              isHelperTextRequired={false}
              content=""
            />
            <TextField
              inputProps={{ "data-testid": "useremail", style: { fontSize: 12, padding: '4px 6px', height: 30 } }}
              {...register("userEmail", {
                required: true,
              })}
              name="userEmail"
              value={user.userEmail ?? ""}
              size="small"
              label="Email"
              fullWidth
              required
              onChange={handleInputChange}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ mb: 2 }}>
            <RequiredLabel
              labelText="Company"
              isHelperTextRequired={false}
              content=""
            />
            <TextField
              inputProps={{ "data-testid": "company", style: { fontSize: 12, padding: '4px 6px', height: 30 } }}
              {...register("company", {
                required: true,
              })}
              name="company"
              value={user.company ?? ""}
              size="small"
              label="Company"
              fullWidth
              required
              onChange={handleInputChange}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
            />
          </Grid>
          <Grid item xs={6} sx={{ mb: 2 }}>
            <RequiredLabel
              labelText="Role"
              isHelperTextRequired={false}
              content=""
            />
              <TextField
                select
                label="Role"
                data-testid="roleId"
                {...register("roleId", {
                  required: true,
                })}
                required
                value={user.roleId?.toString() ?? ""}
                onChange={handleRoleChange}
                size="small"
                name="roleId"
                fullWidth
                inputProps={{ style: { fontSize: 12, padding: '4px 6px', height: 30 } }}
                sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        fontSize: 12,
                      },
                    },
                  },
                  sx: {
                    fontSize: 12,
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 12 }}>Select Role</MenuItem>
                {userRoles
                  .filter((role) => {
                    if (
                      userRole === UserRoles.Admin &&
                      role.label === UserRoles.SuperAdmin
                    ) {
                      return false;
                    }
                    return true;
                  })
                  .map((role) => (
                    <MenuItem key={role.id} value={role.value} sx={{ fontSize: 12 }}>
                      {role.label}
                    </MenuItem>
                  ))}
              </TextField>
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-end">
          <Button type="submit" variant="contained" data-testid="btnSave">
            Save User
          </Button>
        </Grid>
        {isUserRoleConfigured && (
          <Grid container spacing={2} sx={{ ml: 2, mt: 2 }}>
            <Blurb
              infoText="You have already reached the maximum number of users for this role and location."
              title={"Note:"}
            ></Blurb>
          </Grid>
        )}
        </Box>
      </form>
      {loading && <Spinner size={80} data-testid="spinner" />}
    </>
  );
};

export default AddUser;
